import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import {
  ListSalesOrderRequestFilters,
  ListSalesOrderRequestSortDirection,
  ListSalesOrderRequestSortField,
  ListSalesOrderRequestSortOption,
  SalesOrdersService,
  Status,
} from '@vendasta/sales-orders';
import { Badge, StatusToBadgeMapping } from '@vendasta/sales-ui';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { CompanyAccountService } from '../../snapshot/company-account.service';
import { PartnerDomainService } from '@galaxy/partner';
import { NamespaceResolutionService } from '../../namespace-resolution.service';

interface DisplayOrder {
  orderId: string;
  businessId: string;
  badge: Badge;
  requestedActivation: Date;
}

@Component({
  selector: 'app-sales-orders',
  imports: [
    CommonModule,
    MatExpansionModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    MatIconModule,
    MatCardModule,
    GalaxyBadgeModule,
  ],
  templateUrl: './sales-orders.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss', './sales-orders.component.scss'],
})
export class SalesOrdersComponent implements OnInit {
  private readonly partnerDomainService = inject(PartnerDomainService);
  private readonly namespaceResolverService = inject(NamespaceResolutionService);
  private readonly accountCreationService = inject(CompanyAccountService);
  private readonly salesOrdersService = inject(SalesOrdersService);

  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  protected accountGroupId$ = this.accountCreationService.accountGroupId$;
  protected readonly resellerURL$ = combineLatest([
    this.namespaceResolverService.namespace$.pipe(
      switchMap((namespace) => this.partnerDomainService.getDomain(namespace)),
    ),
    this.accountGroupId$,
  ]).pipe(
    map(
      ([domain, accountGroupId]) =>
        `${domain}/order-management?sales-orders=${encodeURIComponent(JSON.stringify({ businessId: accountGroupId ?? '' }))}`,
    ),
  );

  protected orders$: Observable<DisplayOrder[]>;

  constructor(@Inject('PARTNER_ID') private readonly partnerId$: Observable<string>) {}

  ngOnInit(): void {
    this.accountCreationService.setAccountGroupId(this.data.account_group_id ?? '');
    this.setupOrdersFetching();
  }

  private setupOrdersFetching(): void {
    this.orders$ = combineLatest([this.accountGroupId$, this.partnerId$]).pipe(
      switchMap(([accountGroupId, partnerId]) => {
        if (!accountGroupId) {
          return of(null);
        }
        return this.salesOrdersService.list(
          partnerId,
          new ListSalesOrderRequestFilters({ businessId: accountGroupId }),
          new ListSalesOrderRequestSortOption({
            field: ListSalesOrderRequestSortField.CREATED,
            direction: ListSalesOrderRequestSortDirection.DESCENDING,
          }),
          '',
          5,
        );
      }),
      map((resp) => {
        const results = resp?.results || [];
        return results.map((order) => {
          return {
            businessId: order?.businessId || '',
            orderId: order?.orderId || '',
            badge: StatusToBadgeMapping.get(order?.status || Status.SUBMITTED),
            requestedActivation: order?.requestedActivation,
          } as DisplayOrder;
        });
      }),
    );
  }
}
